import React, { createContext, useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { apiDelete } from '~/util/api'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

const CoachDashboardContext = createContext()

export const useCoachDashboard = () => {
  return useContext(CoachDashboardContext)
}

const queryClient = new QueryClient()

const CoachDashboardProvider = ({
  current_user,
  dashboard_data,
  upcoming_events,
  requested_events,
  notifications,
  companyResearchPath,
  hideCancelledMeetings: initialHideCancelledMeetings,
  children,
}) => {
  const schema = z.object({
    search: z.string().optional(),
    category: z.string().optional(),
  })

  const form = useForm({
    defaultValues: {
      viewBy: 'Today',
      candidateSearch: '',
    },
    resolver: zodResolver(schema),
  })

  const { watch } = form

  const viewBy = watch('viewBy')
  const candidateSearch = watch('candidateSearch')

  const [requestedEvents, setRequestedEvents] = React.useState(requested_events)
  const [showColorKeyDialog, setShowColorKeyDialog] = useState(false)
  const toggleShowColorKeyDialog = () =>
    setShowColorKeyDialog(!showColorKeyDialog)
  const [viewableNotifications, setViewableNotifications] =
    useState(notifications)
  const [unfilteredDashboardData, setUnfilteredDashboardData] =
    useState(dashboard_data)

  const [dashboardData, setDashboardData] = React.useState(dashboard_data)

  const [hideCancelledMeetings, setHideCancelledMeetings] = useState(
    initialHideCancelledMeetings,
  )

  useEffect(() => {
    // Filter dashboard_data based on candidateSearch
    const filteredData = {
      meetings: unfilteredDashboardData?.meetings?.filter((meeting) =>
        meeting.candidate.toLowerCase().includes(candidateSearch.toLowerCase()),
      ),
      actionsRequired: unfilteredDashboardData?.actionsRequired?.filter(
        (action) =>
          action.candidate
            .toLowerCase()
            .includes(candidateSearch.toLowerCase()),
      ),
      unnotedEvents: unfilteredDashboardData?.unnotedEvents?.filter((event) =>
        event.candidate.toLowerCase().includes(candidateSearch.toLowerCase()),
      ),
      pastDueTasks: unfilteredDashboardData?.pastDueTasks?.filter((task) =>
        task.candidate.toLowerCase().includes(candidateSearch.toLowerCase()),
      ),
    }

    // Get today's date
    const today = new Date()
    const todayISO = today.toISOString().split('T')[0] // YYYY-MM-DD format

    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    const tomorrowISO = tomorrow.toISOString().split('T')[0]

    const next3Days = new Date(today)
    next3Days.setDate(today.getDate() + 3)
    const next3DaysISO = next3Days.toISOString().split('T')[0]

    const next7Days = new Date(today)
    next7Days.setDate(today.getDate() + 7)
    const next7DaysISO = next7Days.toISOString().split('T')[0]

    // Filter by viewBy
    if (viewBy === 'Today') {
      filteredData.meetings = filteredData?.meetings?.filter((meeting) =>
        meeting.meeting_time.startsWith(todayISO),
      )
      filteredData.actionsRequired = filteredData?.actionsRequired?.filter(
        (action) =>
          !action.due_at ||
          action.due_at.startsWith(todayISO) ||
          action.due_at <= todayISO,
      )
    } else if (viewBy === 'Tomorrow') {
      filteredData.meetings = filteredData?.meetings?.filter((meeting) =>
        meeting.meeting_time.startsWith(tomorrowISO),
      )
      filteredData.actionsRequired = filteredData?.actionsRequired?.filter(
        (action) =>
          !action.due_at ||
          action.due_at.startsWith(tomorrowISO) ||
          action.due_at <= tomorrowISO,
      )
    } else if (viewBy === 'Next 3 Days') {
      filteredData.meetings = filteredData?.meetings?.filter((meeting) => {
        const meetingDate = meeting.meeting_time.split('T')[0]
        return meetingDate >= todayISO && meetingDate <= next3DaysISO
      })
      filteredData.actionsRequired = filteredData?.actionsRequired?.filter(
        (action) => {
          const dueDate = action.due_at ? action.due_at.split('T')[0] : null
          return !dueDate || (dueDate >= todayISO && dueDate <= next3DaysISO)
        },
      )
    } else if (viewBy === 'Next 7 Days') {
      filteredData.meetings = filteredData?.meetings?.filter((meeting) => {
        const meetingDate = meeting.meeting_time.split('T')[0]
        return meetingDate >= todayISO && meetingDate <= next7DaysISO
      })
      filteredData.actionsRequired = filteredData?.actionsRequired?.filter(
        (action) => {
          const dueDate = action.due_at ? action.due_at.split('T')[0] : null
          return !dueDate || (dueDate >= todayISO && dueDate <= next7DaysISO)
        },
      )
    }

    // sort each array in ascending order by date/time.
    filteredData.meetings = filteredData.meetings?.sort(
      (a, b) => new Date(a.meeting_time) - new Date(b.meeting_time),
    )
    filteredData.actionsRequired = filteredData.actionsRequired?.sort(
      (a, b) => new Date(a.due_at || 0) - new Date(b.due_at || 0),
    )
    filteredData.unnotedEvents = filteredData.unnotedEvents?.sort(
      (a, b) => new Date(a.event_time) - new Date(b.event_time),
    )
    filteredData.pastDueTasks = filteredData.pastDueTasks?.sort(
      (a, b) => new Date(a.due_at || 0) - new Date(b.due_at || 0),
    )

    // Update the state with the filtered data
    setDashboardData(filteredData)
  }, [
    viewBy,
    candidateSearch,
    dashboard_data?.meetings,
    dashboard_data?.actionsRequired,
    dashboard_data?.unnotedEvents,
    dashboard_data?.pastDueTasks,
    unfilteredDashboardData,
  ])

  const handleAvailabilityUpdate = async (id, available) => {
    try {
      const response = await fetch(
        `/coach/live_events/${id}/update_availability?available=${available}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to update availability')
      }
      const data = await response.json()
      setRequestedEvents(data)
      console.log('Availability updated successfully')
    } catch (error) {
      console.error('Error updating availability:', error)
    }
  }

  const handleArchive = async (notificationId) => {
    try {
      const response = await apiDelete(
        `/coach/internal_alert_recipients/${current_user.id}`,
        {
          data: {
            internal_alert_id: notificationId,
          },
        },
      )

      if (response.status === 200) {
        setViewableNotifications(JSON.parse(response.data.internal_alerts)) // Update notifications with the returned value
      } else {
        throw new Error('Failed to archive notification')
      }
    } catch (error) {
      console.error('Error archiving notification:', error)
    }
  }

  const handleCancelMeeting = async (serviceRequestId, cancellationMessage) => {
    try {
      const response = await apiDelete(
        `/coach/cancel_meeting?service_request_id=${serviceRequestId}&cancellation_message=${encodeURIComponent(cancellationMessage)}`,
      )
      if (response.status === 200) {
        setDashboardData((prevData) => ({
          ...prevData,
          meetings: prevData.meetings.filter(
            (meeting) => meeting.id !== serviceRequestId,
          ),
          actionsRequired: prevData.actionsRequired.filter(
            (action) => action.id !== serviceRequestId,
          ),
        }))
        setUnfilteredDashboardData((prevData) => ({
          ...prevData,
          meetings: prevData.meetings.filter(
            (meeting) => meeting.id !== serviceRequestId,
          ),
          actionsRequired: prevData.actionsRequired.filter(
            (action) => action.id !== serviceRequestId,
          ),
        }))
      }
    } catch (error) {
      console.error('Error cancelling meeting:', error)
    }
  }
  const values = {
    current_user,
    dashboardData,
    form,
    viewBy,
    upcomingEvents: upcoming_events,
    requestedEvents,
    handleAvailabilityUpdate,
    showColorKeyDialog,
    setShowColorKeyDialog,
    toggleShowColorKeyDialog,
    notifications: viewableNotifications,
    handleArchive,
    handleCancelMeeting,
    unfilteredDashboardData,
    companyResearchPath,
    setUnfilteredDashboardData,
    setDashboardData,
    hideCancelledMeetings,
    setHideCancelledMeetings,
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CoachDashboardContext.Provider value={values}>
        {children}
      </CoachDashboardContext.Provider>
    </QueryClientProvider>
  )
}

export default CoachDashboardProvider
